import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import VehicleSummaryReportService from "../../services/vehicleSummaryReportService";
import utility from "./../../utils/utility";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import XLSX from "xlsx-js-style";
import logo from "../../assets/images/deviops_logo.png";
import { getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function VehicleSummaryReport() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  const getFirstDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    const monthStr = String(month).padStart(2, "0");
    const dayStr = String(lastDay).padStart(2, "0");
    return `${year}-${monthStr}-${dayStr}`;
  };

  // Filters
  const [vehicleType, setVehicleType] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth());
  const [toDate, setToDate] = useState(getLastDayOfMonth());

  // Master lists
  const [rawProjects, setRawProjects] = useState([]);
  const [rawVehicleTypes, setRawVehicleTypes] = useState([]);
  const [rawVehicles, setRawVehicles] = useState([]);
  const [relations, setRelations] = useState([]);

  // Report Data
  const [headerData, setHeaderData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [chargesData, setChargesData] = useState({});
  const [projectSummaryList, setProjectSummaryList] = useState([]);
  const [readingList, setReadingList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const loadFilterOptions = async () => {
    try {
      const activeCompany = StorageService.activeCompany();
      const companyId = activeCompany?.companyId;
      if (!companyId) return;

      const res = await VehicleSummaryReportService.getFilterOptions(companyId);
      if (res?.status && res.data) {
        setRawProjects(res.data.projects || []);
        setRawVehicleTypes(res.data.vehicleTypes || []);
        setRawVehicles(res.data.vehicles || []);
        setRelations(res.data.relations || []);
      }
    } catch (error) {
      console.error("Failed to load filter options", error);
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Filtered dropdown options based on dependencies
  const availableVehicles = rawVehicles.filter((v) => {
    if (vehicleType && String(v.vehicleTypeId) !== String(vehicleType)) {
      return false;
    }
    if (selectedProjectId) {
      const worksInProject = relations.some(
        (r) =>
          String(r.vehicleId) === String(v.id) &&
          String(r.projectId) === String(selectedProjectId)
      );
      if (!worksInProject && relations.length > 0) {
        return false;
      }
    }
    return true;
  });

  const availableVehicleTypes = rawVehicleTypes.filter((vt) => {
    if (selectedProjectId) {
      const hasVehInProj = relations.some(
        (r) =>
          String(r.vehicleTypeId) === String(vt.id) &&
          String(r.projectId) === String(selectedProjectId)
      );
      if (!hasVehInProj && relations.length > 0) {
        return false;
      }
    }
    return true;
  });

  const availableProjects = rawProjects.filter((p) => {
    if (selectedVehicleId) {
      const hasReading = relations.some(
        (r) =>
          String(r.projectId) === String(p.id) &&
          String(r.vehicleId) === String(selectedVehicleId)
      );
      if (!hasReading && relations.length > 0) {
        return false;
      }
    }
    if (vehicleType) {
      const hasTypeReading = relations.some(
        (r) =>
          String(r.projectId) === String(p.id) &&
          String(r.vehicleTypeId) === String(vehicleType)
      );
      if (!hasTypeReading && relations.length > 0) {
        return false;
      }
    }
    return true;
  });

  const handleVehicleTypeChange = (e) => {
    const val = e.target.value;
    setVehicleType(val);
    if (selectedVehicleId) {
      const matched = rawVehicles.find((v) => String(v.id) === String(selectedVehicleId));
      if (matched && String(matched.vehicleTypeId) !== String(val) && val !== "") {
        setSelectedVehicleId("");
      }
    }
  };

  const handleVehicleChange = (e) => {
    const val = e.target.value;
    setSelectedVehicleId(val);
    if (val) {
      const matched = rawVehicles.find((v) => String(v.id) === String(val));
      if (matched && matched.vehicleTypeId) {
        setVehicleType(matched.vehicleTypeId);
      }
    }
  };

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setSelectedProjectId(val);
  };

  const handleClearFilters = () => {
    setVehicleType("");
    setSelectedVehicleId("");
    setSelectedProjectId("");
    setFromDate(getFirstDayOfMonth());
    setToDate(getLastDayOfMonth());
    setHeaderData({});
    setSummaryData({});
    setChargesData({});
    setProjectSummaryList([]);
    setReadingList([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    if (!selectedVehicleId) {
      showMessage("Please select a Vehicle to view report", "error");
      return;
    }

    if (!fromDate) {
      showMessage("From Date is required", "error");
      return;
    }

    if (!toDate) {
      showMessage("To Date is required", "error");
      return;
    }

    setLoading(true);
    try {
      const activeCompany = StorageService.activeCompany();
      const companyId = activeCompany?.companyId;

      const res = await VehicleSummaryReportService.getVehicleSummaryReport({
        companyId,
        vehicleId: selectedVehicleId,
        vehicleTypeId: vehicleType || null,
        projectId: selectedProjectId || null,
        fromDate,
        toDate
      });

      if (res?.status && res.data) {
        setHeaderData(res.data.header || {});
        setSummaryData(res.data.summary || {});
        setChargesData(res.data.charges || {});
        setProjectSummaryList(res.data.projectSummary || []);
        setReadingList(res.data.table || []);
        setHasSearched(true);
        setCurrentPage(1);
      } else {
        showMessage(res?.message || "No records found", "error");
        setHeaderData({});
        setSummaryData({});
        setChargesData({});
        setProjectSummaryList([]);
        setReadingList([]);
        setHasSearched(true);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error(error);
      showMessage("Error fetching vehicle summary report", "error");
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalRecords = readingList.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const paginatedReadingList = readingList.slice(startIndex, endIndex);

  // Helper formatting methods
  const formatNumber = (number, decimals = 2) => {
    const parsed = Number(number);
    if (!Number.isFinite(parsed)) return "0.00";
    return parsed.toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatReading = (number) => {
    const parsed = Number(number);
    if (!Number.isFinite(parsed)) return "0.00";
    return parsed.toFixed(2);
  };

  const calculateReading = (row) => {
    const backendTotal = Number(row.total || row.totalReading || "");
    const rawStart = row.fromReading !== null && row.fromReading !== undefined && row.fromReading !== "" ? row.fromReading : row.start;
    const rawEnd = row.toReading !== null && row.toReading !== undefined && row.toReading !== "" ? row.toReading : row.end;

    if (Number.isFinite(backendTotal) && backendTotal !== 0) {
      return backendTotal;
    }

    if (rawStart !== "" && rawStart !== null && rawEnd !== "" && rawEnd !== null) {
      const start = Number(rawStart);
      const end = Number(rawEnd);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        return end - start;
      }
    }

    return Number.isFinite(backendTotal) ? backendTotal : 0;
  };

  // PDF Export
  const handleExportPDF = async () => {
    if (!selectedVehicleId) {
      showMessage("Vehicle is required for export", "error");
      return;
    }

    if (!fromDate || !toDate) {
      showMessage("Start and End dates are required", "error");
      return;
    }

    try {
      const activeCompany = StorageService.activeCompany();
      const companyId = activeCompany?.companyId;

      const res = await VehicleSummaryReportService.getVehicleSummaryReport({
        companyId,
        vehicleId: selectedVehicleId,
        vehicleTypeId: vehicleType || null,
        projectId: selectedProjectId || null,
        fromDate,
        toDate
      });

      if (!res?.data || !res.data.table || res.data.table.length === 0) {
        showMessage("No data found for PDF export", "error");
        return;
      }

      const { header = {}, summary = {}, charges = {}, projectSummary = [], table = [] } = res.data;

      const doc = new jsPDF("landscape", "mm", "a4");
      const pdfLogo = await getPdfLogoDataUrl(logo);
      const companyName = activeCompany?.companyName || "Devi Projects";
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageMargin = 10;
      const left = pageMargin;
      const right = pageWidth - pageMargin;
      const printableWidth = right - left;
      const footerTop = pageHeight - 25;

      const value = (...items) => {
        const item = items.find((v) => v !== undefined && v !== null && v !== "");
        return item === undefined ? "" : item;
      };

      const formatPdfDate = (date) => (date ? utility.formatDateDDMMMYYYY(date) : "");

      const cell = (x, y, w, h, text, options = {}) => {
        const { bold = false, align = "left", fillColor, fontSize = 8, color = [0, 0, 0] } = options;
        if (fillColor) {
          doc.setFillColor(...fillColor);
          doc.rect(x, y, w, h, "FD");
        } else {
          doc.rect(x, y, w, h);
        }

        const rawVal = String(value(text, ""));
        if (!rawVal) return;

        const maxW = Math.max(1, w - 2.6);

        doc.setFontSize(fontSize);
        doc.setTextColor(...color);
        doc.setFont(undefined, bold ? "bold" : "normal");

        // Split text dynamically: if it fits on a single line, keep as-is
        let lines = [];
        if (Array.isArray(text)) {
          lines = text;
        } else if (rawVal.includes("\n")) {
          lines = rawVal.split("\n");
        } else if (doc.getTextWidth(rawVal) <= maxW) {
          // Fits within cell: keep on single line as before (e.g., "Mallesh (Mallesh)")
          lines = [rawVal];
        } else {
          // Large name that exceeds cell width: split up and down
          if (rawVal.includes(" (") && rawVal.includes(")")) {
            const splitIdx = rawVal.indexOf(" (");
            const topPart = rawVal.slice(0, splitIdx).trim();
            const bottomPart = rawVal.slice(splitIdx).trim();
            lines = [topPart, bottomPart];
          } else {
            lines = doc.splitTextToSize(rawVal, maxW);
          }
        }

        // Further wrap any individual line that still exceeds available cell width
        let finalLines = [];
        lines.forEach((l) => {
          const trimmed = String(l || "").trim();
          if (trimmed && doc.getTextWidth(trimmed) > maxW) {
            finalLines.push(...doc.splitTextToSize(trimmed, maxW));
          } else if (trimmed) {
            finalLines.push(trimmed);
          }
        });

        if (finalLines.length === 0) return;

        // Dynamic font size & line spacing: single-line keeps standard fontSize (8)
        let effFontSize = fontSize;
        let lineSpacing = 2.8;

        if (finalLines.length === 2) {
          effFontSize = Math.min(fontSize, 6.8);
          lineSpacing = 2.8;
        } else if (finalLines.length > 2) {
          effFontSize = Math.min(fontSize, 5.8);
          lineSpacing = 2.2;
        }

        doc.setFontSize(effFontSize);

        let startY;
        if (finalLines.length === 1) {
          startY = y + h / 2 + (effFontSize * 0.3527 / 2) + 0.2;
        } else {
          const totalBlockHeight = (finalLines.length - 1) * lineSpacing;
          startY = y + (h - totalBlockHeight) / 2 + (effFontSize * 0.3527 / 2) - 0.2;
        }

        finalLines.forEach((lineText, idx) => {
          const textX = align === "right"
            ? x + w - 1.5
            : align === "center"
            ? x + w / 2
            : x + 1.3;
          doc.text(String(lineText), textX, startY + idx * lineSpacing, { align });
        });

        doc.setTextColor(0, 0, 0);
      };

      const drawHeader = (pageNumber) => {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.2);

        // Logo & Company Name
        doc.setFont(undefined, "bold");
        doc.setFontSize(12);
        const companyNameWidth = doc.getTextWidth(companyName);
        const logoWidth = 5;
        const logoHeight = 10;
        const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
        const logoY = 8;

        doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
        doc.text(companyName, left + 6, 23);

        // Report Date
        const reportPrefix = "Report Date: ";
        const reportDateStr = utility.formatExportDateTime(new Date());
        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        const prefixWidth = doc.getTextWidth(reportPrefix);
        doc.setFont(undefined, "normal");
        const dateWidth = doc.getTextWidth(reportDateStr);
        const totalWidth = prefixWidth + dateWidth;
        const startX = right - totalWidth;

        doc.setFont(undefined, "bold");
        doc.text(reportPrefix, startX, 23);
        doc.setFont(undefined, "normal");
        doc.text(reportDateStr, startX + prefixWidth, 23);

        // Title (Centered, Underlined)
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Vehicle Summary Report", pageWidth / 2, 35, { align: "center" });
        doc.line(pageWidth / 2 - 40, 37, pageWidth / 2 + 40, 37);

        // First page top boxes (NO Project Name row, starts directly at infoY)
        if (pageNumber === 1) {
          const infoRowH = 8;
          const rowH = 7;
          const infoY = 44;

          const widths = [32, 33, 26, 30, 40, 39, 44, 33];
          const xs = widths.reduce((acc, w, index) => {
            acc.push(index === 0 ? left : acc[index - 1] + widths[index - 1]);
            return acc;
          }, []);

          [
            [
              "Vehicle Type:",
              value(header.vehicleType),
              "Vehicle No:",
              value(header.vehicleNo),
              "Vendor(Vehicle Owner):",
              value(header.vehicleOwner, header.vendorName),
              "Reading Type:",
              value(header.readingType)
            ],
            [
              "Hire Date:",
              formatPdfDate(header.hireStartDate),
              "Hire Type:",
              value(header.hireType),
              "Hire Rate:",
              formatNumber(value(header.hireRate, 0)),
              "Fuel Borne By:",
              value(header.fuelBorneBy)
            ]
          ].forEach((row, rowIndex) => {
            row.forEach((text, index) => {
              cell(xs[index], infoY + rowIndex * infoRowH, widths[index], infoRowH, text, {
                bold: index % 2 === 0
              });
            });
          });

          // Summary & Charges Boxes
          const sumY = 61;
          const sumWidths = [39, 35, 31, 33, 32, 28];
          const sumXs = sumWidths.reduce((acc, w, index) => {
            acc.push(index === 0 ? left : acc[index - 1] + sumWidths[index - 1]);
            return acc;
          }, []);

          [
            [
              "Working Days:",
              value(summary.workingDays, 0),
              "Idle Days:",
              value(summary.idleDays, 0),
              "Break Down Days:",
              value(summary.breakdownDays, 0)
            ],
            [
              "Start Date:",
              formatPdfDate(fromDate),
              "End Date:",
              formatPdfDate(toDate),
              "Total Days:",
              value(summary.totalDays, 0)
            ],
            [
              "Start Reading:",
              formatReading(value(summary.startReading, 0)),
              "End Reading:",
              formatReading(value(summary.endReading, 0)),
              "Total Reading:",
              formatReading(value(summary.totalReading, 0))
            ],
            [
              value(summary.fuelType, header.fuelType)
                ? `Total Fuel(Ltrs) (${value(summary.fuelType, header.fuelType)}):`
                : "Total Fuel(Ltrs):",
              formatNumber(value(summary.totalFuelLiters, 0)),
              "Total Fuel Amt:",
              formatNumber(value(summary.totalFuelAmount, 0)),
              "Vehicle Avg:",
              formatNumber(value(summary.vehicleAvg, 0))
            ]
          ].forEach((row, rowIndex) => {
            row.forEach((text, index) => {
              cell(sumXs[index], sumY + rowIndex * rowH, sumWidths[index], rowH, text, {
                bold: index % 2 === 0
              });
            });
          });

          // Overall Charges Box (Right Side)
          const chargeY = 61;
          const chargeX = 210;
          const chargeCol1W = 44;
          const chargeCol2W = right - chargeX - chargeCol1W;

          doc.setLineWidth(0.6);
          [
            ["Total Hire Charges:", formatNumber(value(charges.totalHire, 0))],
            ["Fuel Charges:", formatNumber(value(charges["fuel-charges"], 0))],
            ["Balance Payable:", formatNumber(value(charges.balancePayable, 0))]
          ].forEach((row, index) => {
            cell(chargeX, chargeY + index * rowH, chargeCol1W, rowH, row[0], { bold: true });
            cell(chargeX + chargeCol1W, chargeY + index * rowH, chargeCol2W, rowH, row[1], {
              bold: true,
              align: "right"
            });
          });
          doc.setLineWidth(0.2);
        }
      };

      const drawPageNumber = (pageNumber, totalPages) => {
        doc.setFont(undefined, "normal");
        doc.setFontSize(8);
        const text = totalPages ? `Page ${pageNumber} of ${totalPages}` : `Page ${pageNumber}`;
        doc.text(text, pageWidth / 2, pageHeight - 7, { align: "center" });
      };

      const drawSignatureHeadings = () => {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        doc.text("Prepared By", left + 30, footerTop + 4);
        doc.text("Checked By", pageWidth / 2, footerTop + 4, { align: "center" });
        doc.text("Approved By", right - 30, footerTop + 4, { align: "center" });
      };

      // 1. Draw Page 1 header elements
      drawHeader(1);

      // 2. Project-Wise Summary Table
      let currentY = 97;
      if (projectSummary.length > 0) {
        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Project-Wise Summary", left, currentY);
        currentY += 3.5;

        const projTableBody = projectSummary.map((ps) => [
          ps.formattedProjectName || ps.projectName,
          ps.leaseType || "-",
          formatNumber(ps.hireRate),
          ps.fuelBorneBy || "-",
          ps.workingDays,
          ps.idleDays,
          ps.breakdownDays,
          ps.totalDays,
          formatReading(ps.totalReading),
          formatNumber(ps.fuelLiters),
          formatNumber(ps.fuelAmount),
          formatNumber(ps.totalHire),
          formatNumber(ps.fuelCharges),
          formatNumber(ps.balancePayable)
        ]);

        const projTotals = [
          { content: "Total", colSpan: 8, styles: { halign: "center", fontStyle: "bold" } },
          formatReading(projectSummary.reduce((s, p) => s + (Number(p.totalReading) || 0), 0)),
          formatNumber(projectSummary.reduce((s, p) => s + (Number(p.fuelLiters) || 0), 0)),
          formatNumber(projectSummary.reduce((s, p) => s + (Number(p.fuelAmount) || 0), 0)),
          formatNumber(projectSummary.reduce((s, p) => s + (Number(p.totalHire) || 0), 0)),
          formatNumber(projectSummary.reduce((s, p) => s + (Number(p.fuelCharges) || 0), 0)),
          formatNumber(projectSummary.reduce((s, p) => s + (Number(p.balancePayable) || 0), 0))
        ];

        autoTable(doc, {
          startY: currentY,
          margin: { top: 45, left, right: pageMargin, bottom: 25 },
          head: [
            [
              "Project",
              "Hire Type",
              "Rate",
              "Fuel By",
              "Work Days",
              "Idle Days",
              "BD Days",
              "Total Days",
              "Total Reading",
              "Fuel(Ltrs)",
              "Fuel Amt",
              "Hire Charges",
              "Fuel Deduct",
              "Balance"
            ]
          ],
          body: projTableBody,
          foot: [projTotals],
          theme: "grid",
          styles: {
            fontSize: 7.5,
            cellPadding: 1,
            lineColor: [0, 0, 0],
            lineWidth: 0.15,
            textColor: [0, 0, 0]
          },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center"
          },
          footStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "right"
          },
          columnStyles: {
            0: { halign: "left" },
            1: { halign: "center" },
            2: { halign: "right" },
            3: { halign: "center" },
            4: { halign: "right" },
            5: { halign: "right" },
            6: { halign: "right" },
            7: { halign: "right" },
            8: { halign: "right" },
            9: { halign: "right" },
            10: { halign: "right" },
            11: { halign: "right" },
            12: { halign: "right" },
            13: { halign: "right" }
          }
        });

        currentY = doc.lastAutoTable.finalY + 8;
      }

      // 3. Detailed Readings Table (with Project column)
      const sortedTable = [...table].sort((a, b) => {
        const dateA = new Date(a.date || a.readingDate || 0).getTime();
        const dateB = new Date(b.date || b.readingDate || 0).getTime();
        return dateA - dateB;
      });

      const tableData = sortedTable.map((r) => {
        const startReadingDisplay =
          r.fromReading !== null && r.fromReading !== "" && r.fromReading !== undefined
            ? formatReading(r.fromReading)
            : r.startTime
            ? utility.formatDbDateTime(r.startTime)
            : "-";
        const endReadingDisplay =
          r.toReading !== null && r.toReading !== "" && r.toReading !== undefined
            ? formatReading(r.toReading)
            : r.endTime
            ? utility.formatDbDateTime(r.endTime)
            : "-";

        return [
          formatPdfDate(r.date || r.readingDate),
          r.formattedProjectName || r.projectName || "-",
          r.status || r.workingStatus || "-",
          r.readingType || header.readingType || "-",
          startReadingDisplay,
          endReadingDisplay,
          formatReading(calculateReading(r)),
          formatNumber(r.fuelLiters || 0),
          formatNumber(r.rate || 0),
          formatNumber(r.fuelAmount || 0),
          r.driverName || "-",
          r.notes || r.workDescription || ""
        ];
      });

      const totalReadingSum = formatReading(sortedTable.reduce((sum, row) => sum + calculateReading(row), 0));
      const totalFuelSum = formatNumber(sortedTable.reduce((sum, row) => sum + Number(row.fuelLiters || 0), 0));
      const totalFuelAmountSum = formatNumber(sortedTable.reduce((sum, row) => sum + Number(row.fuelAmount || 0), 0));

      autoTable(doc, {
        startY: currentY,
        margin: { top: 45, left, right: pageMargin, bottom: 25 },
        head: [
          [
            "Date",
            "Project",
            "Work Status",
            "Reading Type",
            "Start Reading",
            "End Reading",
            "Total Reading",
            "Fuel(Ltrs)",
            "Rate/Ltr",
            "Fuel Amount",
            "Driver Name",
            "Notes"
          ]
        ],
        body: tableData,
        foot: [["", "", "", "", "", "Total:", totalReadingSum, totalFuelSum, "", totalFuelAmountSum, "", ""]],
        showFoot: "lastPage",
        theme: "grid",
        styles: {
          fontSize: 7.5,
          cellPadding: 1,
          lineColor: [0, 0, 0],
          lineWidth: 0.15,
          textColor: [0, 0, 0],
          minCellHeight: 5
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center"
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        footStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "right"
        },
        columnStyles: {
          0: { cellWidth: 18, halign: "center" },
          1: { cellWidth: 38, halign: "left" },
          2: { cellWidth: 18, halign: "left" },
          3: { cellWidth: 20, halign: "left" },
          4: { cellWidth: 24, halign: "right" },
          5: { cellWidth: 24, halign: "right" },
          6: { cellWidth: 20, halign: "right" },
          7: { cellWidth: 18, halign: "right" },
          8: { cellWidth: 16, halign: "right" },
          9: { cellWidth: 22, halign: "right" },
          10: { cellWidth: 24, halign: "left" },
          11: { cellWidth: "auto", halign: "left" }
        },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            drawHeader(data.pageNumber);
          }
        }
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        if (i === pageCount) {
          drawSignatureHeadings();
        }
        drawPageNumber(i, pageCount);
      }

      doc.save(`Vehicle_Summary_Report_${header.vehicleNo || "Export"}.pdf`);
    } catch (error) {
      console.error("PDF export error:", error);
      showMessage("Error exporting PDF", "error");
    }
  };

  // Excel Export
  const handleExportExcel = async () => {
    if (!selectedVehicleId) {
      showMessage("Vehicle is required for export", "error");
      return;
    }

    if (!fromDate || !toDate) {
      showMessage("Start and End dates are required", "error");
      return;
    }

    try {
      const activeCompany = StorageService.activeCompany();
      const companyId = activeCompany?.companyId;

      const res = await VehicleSummaryReportService.getVehicleSummaryReport({
        companyId,
        vehicleId: selectedVehicleId,
        vehicleTypeId: vehicleType || null,
        projectId: selectedProjectId || null,
        fromDate,
        toDate
      });

      if (!res?.data || !res.data.table || res.data.table.length === 0) {
        showMessage("No data available for export", "error");
        return;
      }

      const { header = {}, summary = {}, charges = {}, projectSummary = [], table = [] } = res.data;

      const pickValue = (...items) => {
        const item = items.find((v) => v !== undefined && v !== null && v !== "");
        return item === undefined ? "" : item;
      };

      const sortedTable = [...table].sort((a, b) => {
        const dateA = new Date(a.date || a.readingDate || 0).getTime();
        const dateB = new Date(b.date || b.readingDate || 0).getTime();
        return dateA - dateB;
      });

      const totalReadingSum = sortedTable.reduce((sum, row) => sum + calculateReading(row), 0);
      const totalFuelSum = sortedTable.reduce((sum, row) => sum + Number(pickValue(row.fuelLiters, 0)), 0);
      const totalFuelAmountSum = Number(pickValue(summary.totalFuelAmount, sortedTable.reduce((sum, row) => sum + Number(pickValue(row.fuelAmount, 0)), 0))) || 0;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      const thin = { style: "thin" };
      const cellBorder = { top: thin, bottom: thin, left: thin, right: thin };
      const center = { horizontal: "center", vertical: "center", wrapText: true };
      const leftAlign = { horizontal: "left", vertical: "center", wrapText: true };
      const rightAlign = { horizontal: "right", vertical: "center" };
      const labelStyle = { font: { bold: true, sz: 10 }, alignment: leftAlign, border: cellBorder };
      const valueStyle = { font: { sz: 10 }, alignment: leftAlign, border: cellBorder };
      const numStyle = { font: { sz: 10 }, alignment: rightAlign, border: cellBorder };
      const tableHeaderStyle = {
        font: { bold: true, sz: 10 },
        alignment: center,
        border: cellBorder,
        fill: { fgColor: { rgb: "FFD9D9D9" } }
      };

      const styleCells = (r, colCount, styleForCol) => {
        for (let c = 0; c < colCount; c++) {
          const cellKey = XLSX.utils.encode_cell({ r, c });
          if (!ws[cellKey]) ws[cellKey] = { v: "", t: "s" };
          ws[cellKey].s = styleForCol(c);
        }
      };

      const addPairRows = (rows) => {
        rows.forEach((pairs) => {
          const colCount = pairs.length * 2;
          const rowData = new Array(colCount).fill("");
          pairs.forEach(([label, val], idx) => {
            rowData[idx * 2] = label;
            rowData[idx * 2 + 1] = val;
          });
          XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
          styleCells(row, colCount, (c) => (c % 2 === 0 ? labelStyle : valueStyle));

          pairs.forEach(([, val], idx) => {
            const colIdx = idx * 2 + 1;
            const cellKey = XLSX.utils.encode_cell({ r: row, c: colIdx });
            if (ws[cellKey] && typeof val === "number") {
              ws[cellKey].v = val;
              ws[cellKey].t = "n";
              ws[cellKey].z = "#,##0.00";
              ws[cellKey].s = numStyle;
            }
          });
          row++;
        });
      };

      let row = 0;
      const companyName = activeCompany?.companyName || "Devi Projects";
      const headerReportDateStr = `Report Date: ${utility.formatExportDateTime(new Date())}`;

      // Top Title Block
      XLSX.utils.sheet_add_aoa(ws, [[companyName, "", "", "", "", "", headerReportDateStr, "", "", "", ""]], {
        origin: { r: row, c: 0 }
      });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });
      ws["!merges"].push({ s: { r: row, c: 6 }, e: { r: row, c: 10 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 12 }, alignment: leftAlign };
      ws[XLSX.utils.encode_cell({ r: row, c: 6 })].s = { font: { bold: true, sz: 10 }, alignment: rightAlign };
      row++;

      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Summary Report"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 10 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 16 }, alignment: center };
      row += 2;

    const formatExportDate = (dateVal) => {
      if (!dateVal) return "";
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return String(dateVal);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    };

    // Vehicle Metadata Pairs (Without Project Name row)
    addPairRows([
      [
        ["Vehicle Type:", header.vehicleType || ""],
        ["Vehicle No:", pickValue(header.vehicleNo, "")],
        ["Vendor(Vehicle Owner):", pickValue(header.vehicleOwner, header.vendorName, "")],
        ["Reading Type:", header.readingType || ""]
      ],
      [
        ["Hire Date:", header.hireStartDate ? formatExportDate(header.hireStartDate) : ""],
        ["Hire Type:", pickValue(header.hireType, "")],
        ["Hire Rate:", Number(pickValue(header.hireRate, 0)) || 0],
        ["Fuel Borne By:", header.fuelBorneBy || ""]
      ]
    ]);

    row++;

    // Overall Summary Pairs & Charges side-by-side
    const summaryRows = [
      [
        ["Working Days:", Number(summary.workingDays) || 0, "0"],
        ["Idle Days:", Number(summary.idleDays) || 0, "0"],
        ["Break Down Days:", Number(summary.breakdownDays) || 0, "0"]
      ],
      [
        ["Start Date:", formatExportDate(fromDate)],
        ["End Date:", formatExportDate(toDate)],
        ["Total Days:", Number(summary.totalDays) || 0, "0"]
      ],
      [
        ["Start Reading:", Number(summary.startReading) || 0, "0.00"],
        ["End Reading:", Number(summary.endReading) || 0, "0.00"],
        ["Total Reading:", Number(totalReadingSum) || 0, "0.00"]
      ],
      [
        [
          pickValue(summary.fuelType, header.fuelType)
            ? `Total Fuel(Ltrs) (${pickValue(summary.fuelType, header.fuelType)}):`
            : "Total Fuel(Ltrs):",
          Number(pickValue(summary.totalFuelLiters, totalFuelSum)) || 0,
          "0.00"
        ],
        ["Total Fuel Amt:", Number(totalFuelAmountSum) || 0, "0.00"],
        ["Vehicle Avg:", Number(summary.vehicleAvg) || 0, "0.00"]
      ]
    ];

    const chargesRows = [
      ["Total Hire Charges:", Number(charges.totalHire) || 0],
      ["Fuel Charges:", Number(charges["fuel-charges"]) || 0],
      ["Balance Payable:", Number(charges.balancePayable) || 0]
    ];

    for (let rIdx = 0; rIdx < 4; rIdx++) {
      const sPairs = summaryRows[rIdx];
      const cPair = chargesRows[rIdx];

      // Populate Summary columns (0 to 5)
      sPairs.forEach(([label, val, format], idx) => {
        const colLabel = idx * 2;
        const colVal = idx * 2 + 1;

        const cellLabelKey = XLSX.utils.encode_cell({ r: row, c: colLabel });
        ws[cellLabelKey] = { v: label || "", t: "s", s: labelStyle };

        const cellValKey = XLSX.utils.encode_cell({ r: row, c: colVal });
        if (typeof val === "number") {
          ws[cellValKey] = {
            v: val,
            t: "n",
            z: format || "0.00",
            s: valueStyle
          };
        } else {
          ws[cellValKey] = {
            v: val !== undefined && val !== null ? String(val) : "",
            t: "s",
            s: valueStyle
          };
        }
      });

      // Populate Charges columns (6 and 7) for rows 0, 1, 2
      if (cPair) {
        const [cLabel, cVal] = cPair;
        const cellCLabelKey = XLSX.utils.encode_cell({ r: row, c: 6 });
        ws[cellCLabelKey] = {
          v: cLabel,
          t: "s",
          s: { ...labelStyle, font: { bold: true, sz: 10 } }
        };

        const cellCValKey = XLSX.utils.encode_cell({ r: row, c: 7 });
        ws[cellCValKey] = {
          v: Number(cVal) || 0,
          t: "n",
          z: "#,##0.00",
          s: { ...numStyle, font: { bold: true, sz: 10 }, alignment: rightAlign }
        };
      }

      row++;
    }

      row += 2;

      // Project-Wise Summary Table in Excel
      if (projectSummary.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [["Project-Wise Summary"]], { origin: { r: row, c: 0 } });
        ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 13 } });
        ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 12 }, alignment: leftAlign };
        row++;

        const projHeaders = [
          "Project",
          "Hire Type",
          "Rate",
          "Fuel By",
          "Work Days",
          "Idle Days",
          "BD Days",
          "Total Days",
          "Total Reading",
          "Fuel(Ltrs)",
          "Fuel Amt",
          "Hire Charges",
          "Fuel Deduct",
          "Balance"
        ];
        XLSX.utils.sheet_add_aoa(ws, [projHeaders], { origin: { r: row, c: 0 } });
        styleCells(row, 14, () => tableHeaderStyle);
        row++;

        projectSummary.forEach((ps) => {
          const rowData = [
            ps.formattedProjectName || ps.projectName,
            ps.leaseType || "-",
            Number(ps.hireRate) || 0,
            ps.fuelBorneBy || "-",
            Number(ps.workingDays) || 0,
            Number(ps.idleDays) || 0,
            Number(ps.breakdownDays) || 0,
            Number(ps.totalDays) || 0,
            Number(ps.totalReading) || 0,
            Number(ps.fuelLiters) || 0,
            Number(ps.fuelAmount) || 0,
            Number(ps.totalHire) || 0,
            Number(ps.fuelCharges) || 0,
            Number(ps.balancePayable) || 0
          ];
          XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
          styleCells(row, 14, (c) => {
            if (c === 0) return valueStyle;
            if (c === 1 || c === 3) return { ...valueStyle, alignment: center };
            return numStyle;
          });

          [2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].forEach((c) => {
            const cellKey = XLSX.utils.encode_cell({ r: row, c });
            if (ws[cellKey] && typeof ws[cellKey].v === "number") {
              ws[cellKey].t = "n";
              ws[cellKey].z = (c === 4 || c === 5 || c === 6 || c === 7) ? "0" : (c === 8 ? "0.00" : "#,##0.00");
            }
          });
          row++;
        });

        const projTotals = [
          "Total",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          projectSummary.reduce((s, p) => s + (Number(p.totalReading) || 0), 0),
          projectSummary.reduce((s, p) => s + (Number(p.fuelLiters) || 0), 0),
          projectSummary.reduce((s, p) => s + (Number(p.fuelAmount) || 0), 0),
          projectSummary.reduce((s, p) => s + (Number(p.totalHire) || 0), 0),
          projectSummary.reduce((s, p) => s + (Number(p.fuelCharges) || 0), 0),
          projectSummary.reduce((s, p) => s + (Number(p.balancePayable) || 0), 0)
        ];
        XLSX.utils.sheet_add_aoa(ws, [projTotals], { origin: { r: row, c: 0 } });
        ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 7 } });
        styleCells(row, 14, () => ({ ...tableHeaderStyle, alignment: center }));
        ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 10 }, alignment: center, border: { top: thin, bottom: thin, left: thin, right: thin } };
        for (let c = 8; c <= 13; c++) {
          const cellKey = XLSX.utils.encode_cell({ r: row, c });
          if (ws[cellKey] && typeof ws[cellKey].v === "number") {
            ws[cellKey].t = "n";
            ws[cellKey].z = c === 8 ? "0.00" : "#,##0.00";
            ws[cellKey].s = { font: { bold: true, sz: 10 }, alignment: rightAlign, border: { top: thin, bottom: thin, left: thin, right: thin } };
          }
        }
        row++;

        row++;
      }

      // Detailed Readings Table Header
      const tableHeaders = [
        "Date",
        "Project",
        "Work Status",
        "Reading Type",
        "Start Reading",
        "End Reading",
        "Total Reading",
        "Fuel(Ltrs)",
        "Rate/Ltr",
        "Fuel Amount",
        "Driver Name",
        "Notes"
      ];
      XLSX.utils.sheet_add_aoa(ws, [tableHeaders], { origin: { r: row, c: 0 } });
      styleCells(row, 12, () => tableHeaderStyle);
      row++;

      const toExcelDateSerial = (value) => {
        if (!value) return "-";
        const serial = utility.convertDateToExcelSerial(value);
        return serial !== null ? serial : "-";
      };

      sortedTable.forEach((item) => {
        const rawFrom = pickValue(item.fromReading, item.startReading, null);
        const rawTo = pickValue(item.toReading, item.endReading, null);
        const isNumFrom = rawFrom !== null && rawFrom !== "" && !isNaN(Number(rawFrom));
        const isNumTo = rawTo !== null && rawTo !== "" && !isNaN(Number(rawTo));
        const parsedFrom = isNumFrom ? Number(rawFrom) : item.startTime ? utility.formatExportDateTime(item.startTime) : "-";
        const parsedTo = isNumTo ? Number(rawTo) : item.endTime ? utility.formatExportDateTime(item.endTime) : "-";
        const rawDate = item.date || item.readingDate;
        const dateSerial = toExcelDateSerial(rawDate);

        const rowData = [
          dateSerial,
          pickValue(item.formattedProjectName, item.projectName, "-"),
          pickValue(item.status, item.workingStatus, ""),
          pickValue(item.readingType, header.readingType, "-"),
          parsedFrom,
          parsedTo,
          calculateReading(item),
          Number(pickValue(item.fuelLiters, 0)) || 0,
          Number(pickValue(item.rate, 0)) || 0,
          Number(pickValue(item.fuelAmount, 0)) || 0,
          pickValue(item.driverName, "-"),
          pickValue(item.notes, item.workDescription, "")
        ];
        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
        styleCells(row, 12, (c) => {
          if (c === 0) return { ...valueStyle, alignment: center };
          if (c === 1 || c === 2 || c === 3 || c === 10 || c === 11) return { ...valueStyle, alignment: leftAlign };
          if (c === 4) return isNumFrom ? numStyle : { ...valueStyle, alignment: center };
          if (c === 5) return isNumTo ? numStyle : { ...valueStyle, alignment: center };
          if (c >= 6 && c <= 9) return numStyle;
          return valueStyle;
        });

        const dateCellKey = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (ws[dateCellKey] && typeof ws[dateCellKey].v === "number") {
          ws[dateCellKey].t = "n";
          ws[dateCellKey].z = "dd-mm-yyyy";
        }

        [4, 5, 6, 7, 8, 9].forEach((c) => {
          const cellKey = XLSX.utils.encode_cell({ r: row, c });
          if (ws[cellKey] && typeof ws[cellKey].v === "number") {
            ws[cellKey].t = "n";
            ws[cellKey].z = c === 4 || c === 5 || c === 6 ? "0.00" : "#,##0.00";
          }
        });

        row++;
      });

      // Totals Row
      const totalsRow = [
        "",
        "",
        "",
        "",
        "",
        "Total:",
        Number(totalReadingSum) || 0,
        Number(totalFuelSum) || 0,
        "",
        Number(totalFuelAmountSum) || 0,
        "",
        ""
      ];
      XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });
      styleCells(row, 12, (c) => {
        if (c === 5) return { ...labelStyle, alignment: rightAlign };
        if (c === 6 || c === 7 || c === 9) return { ...numStyle, font: { bold: true } };
        return valueStyle;
      });

      [6, 7, 9].forEach((c) => {
        const cellKey = XLSX.utils.encode_cell({ r: row, c });
        if (ws[cellKey] && typeof ws[cellKey].v === "number") {
          ws[cellKey].t = "n";
          ws[cellKey].z = c === 6 ? "0.00" : "#,##0.00";
        }
      });

      // Col widths
      ws["!cols"] = [
        { wch: 14 },
        { wch: 32 },
        { wch: 16 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 18 },
        { wch: 22 },
        { wch: 26 }
      ];

      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: row, c: 13 }
      });

      XLSX.utils.book_append_sheet(wb, ws, "Vehicle Summary");
      const fileName = `Vehicle_Summary_Report_${header.vehicleNo || "Export"}_${utility.formatDateDDMMMYYYY(new Date())}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Excel export error:", error);
      showMessage("Error exporting to Excel", "error");
    }
  };

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Summary Report" secondTitle="Vehicle Management" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="row">
        <div className="card py-3 bg-white shadow-sm">
          {/* 🔹 Filter Section */}
          <div className="row mb-3">
            {/* Vehicle Type */}
            <div className="col-md-3 mb-2">
              <label className="form-label">Vehicle Type</label>
              <select
                className="form-select"
                value={vehicleType}
                onChange={handleVehicleTypeChange}
              >
                <option value="">Select Vehicle Type</option>
                {availableVehicleTypes.map((vt) => (
                  <option key={vt.id} value={vt.id}>
                    {vt.vehicleType}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Dropdown */}
            <div className="col-md-3 mb-2">
              <label className="form-label">
                Vehicle <span className="text-danger">*</span>
              </label>
              <select
                className="form-select vehicle-scroll-select"
                value={selectedVehicleId}
                onChange={handleVehicleChange}
              >
                <option value="">Select Vehicle</option>
                {availableVehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    ({v.vehicleType || "N/A"}) --- {v.vehicleName} - {v.registrationNo}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Dropdown */}
            <div className="col-md-3 mb-2">
              <label className="form-label">Project</label>
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={handleProjectChange}
              >
                <option value="">Select Project</option>
                {availableProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.formattedName || `${p.projectName}-${p.projectCode}`}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div className="col-md-3 mb-2">
              <label className="form-label">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            {/* To Date */}
            <div className="col-md-3 mb-2">
              <label className="form-label">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          {/* 🔹 Action Buttons */}
          <div className="mb-3 d-flex justify-content-end align-items-center">
            <div className="d-flex gap-2">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                <i className="ti ti-refresh me-1"></i>
                Clear
              </button>

              <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                <i className="ti ti-search me-1"></i>
                {loading ? "Searching..." : "View Report"}
              </button>
            </div>
          </div>

          <hr />

          <div className="mb-3 d-flex justify-content-end align-items-center">
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={handleExportPDF}>
                Export PDF
              </button>

              <button className="btn btn-success" onClick={handleExportExcel}>
                Export Excel
              </button>
            </div>
          </div>

          {/* 🔹 Report Table (Same format and styling as Vehicle Reading Report) */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Reading Date</th>
                  <th>Project</th>
                  <th>Vendor Name</th>
                  <th>Vehicle Type</th>
                  <th>Owner Name</th>
                  <th>Registration No</th>
                  <th>Machine Status</th>
                  <th>Reading Type</th>
                  <th>Start Reading</th>
                  <th>End Reading</th>
                  <th>Total Readings</th>
                  <th>Fuel Amount</th>
                  <th>Hire Type</th>
                  <th>Hire Amount</th>
                  <th>Driver Name</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="16" className="text-center py-4">
                      <div className="spinner-border text-primary"></div>
                    </td>
                  </tr>
                ) : paginatedReadingList.length === 0 ? (
                  <tr>
                    <td colSpan="16" className="text-center text-muted">
                      No vehicle report found
                    </td>
                  </tr>
                ) : (
                  paginatedReadingList.map((r, index) => (
                    <tr key={startIndex + index}>
                      <td>{startIndex + index + 1}</td>
                      <td>{utility.formatDateDDMMMYYYY(r.readingDate || r.date)}</td>
                      <td>{r.formattedProjectName || r.projectName || "-"}</td>
                      <td>{r.vendorName || headerData.vendorName || "-"}</td>
                      <td>{r.vehicleType || headerData.vehicleType || "-"}</td>
                      <td>{r.vehicleName || headerData.vehicleName || "-"}</td>
                      <td>{r.registrationNo || headerData.vehicleNo || "-"}</td>
                      <td>{r.machineStatus || r.status || "-"}</td>
                      <td>{r.readingType || headerData.readingType || "-"}</td>
                      <td>
                        {r.fromReading !== null && r.fromReading !== "" && r.fromReading !== undefined
                          ? r.fromReading
                          : r.startTime
                          ? utility.formatDbDateTime(r.startTime)
                          : "-"}
                      </td>
                      <td>
                        {r.toReading !== null && r.toReading !== "" && r.toReading !== undefined && Number(r.toReading) > 0
                          ? r.toReading
                          : r.endTime
                          ? utility.formatDbDateTime(r.endTime)
                          : "-"}
                      </td>
                      <td>{r.totalReading ? Number(r.totalReading).toFixed(2) : "0.00"}</td>
                      <td>{r.fuelAmount || 0}</td>
                      <td>{r.leaseType || headerData.hireType || "-"}</td>
                      <td>{r.vehicleHirerate || headerData.hireRate || 0}</td>
                      <td>{r.driverName || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 🔹 Pagination */}
          {totalRecords > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              {/* TOTAL RECORDS */}
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              {/* PAGINATION BUTTONS */}
              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)]
                    .map((_, i) => i + 1)
                    .filter((p) => {
                      if (totalPages <= 10) return true;
                      return p === 1 || p === totalPages || (p >= currentPage - 2 && p <= currentPage + 2);
                    })
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span className="px-1 d-flex align-items-center text-muted">...</span>
                        )}
                        <button
                          className={`btn btn-sm ${
                            currentPage === p ? "btn-primary" : "btn-outline-primary"
                          }`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
